// report.hmc_theme.mixin.js
// 테마평가(수자원/생물다양성) 결과보고서 — hmc-theme (campaign 560/561)
// hyundai-v2 결과보고서를 시각적으로 복제하고 내용만 테마 데이터에 맞게 교체.
//   core.generatePDF 가 IS_HMC_THEME 일 때 generatePDF_hmc_theme(doc, position_top) 를 호출한다.
//   doc 는 core 가 생성한 px-unit jsPDF (446.46 × 631.42, setDefaultFont 완료). 재생성하지 않는다.
//   전년도(prior-year) 비교 로직 없음. COVER(htPageCover)는 유지(px 스케일로 재배치).
import autoTable from 'jspdf-autotable';
import ReportCoreMixin from '@shared/mixin/report/report.core.mixin.js';
import { rubric, levelMeta, appendixUrls, riskSummary } from '@shared/mixin/report/raw-data/hmc_theme_2026';
import riskByCompany from '@shared/mixin/report/raw-data/hmc_theme_risk.json';
import overallByCompany from '@shared/mixin/report/raw-data/hmc_theme_overall.json';

// 표지 로고 (현대/기아/QESG)
const HyundaiLogo = require('@shared/assets/img/hyundai_logo.png');
const HyundaiLogoBluePNG = require('@shared/assets/img/hyundai_logo_blue.png');
const KiaLogo = require('@shared/assets/img/kia_logo.png');
const QESGLogoWhitePNG = require('@shared/assets/img/QuantifiedESG_Logo_v3_White.png');

// 자연 크기(px) — addImage aspect 계산용
const DIM = {
    hyundai: [235, 30],
    kia: [1060, 277],
    qesgWhite: [3747, 942]
};

// 경영 수준 → 색상 (hyundai-v2 accentRGB tone 그대로 사용. PPTX 원색 미사용)
// 통일 색 조합 = 파스텔 팔레트 (다홍→주황→옅은초록→진한초록). 밝은 톤이라 글자는 어둡게.
const LEVEL_ACCENT_RGB = {
    insufficient: [255, 154, 134], // #FF9A86 다홍
    average: [255, 193, 147],      // #FFC193 노랑/주황
    excellent: [204, 214, 127],    // #CCD67F 옅은 초록
    outstanding: [121, 174, 111],  // #79AE6F 진한 초록
    na: [230, 230, 230]            // 해당없음 = 연회색(뮤트)
};
const LEVEL_TEXT_RGB = {
    insufficient: [51, 51, 51], average: [51, 51, 51],
    excellent: [51, 51, 51], outstanding: [51, 51, 51], na: [90, 90, 90]
};

const LVL_ORDER = ['insufficient', 'average', 'excellent', 'outstanding', 'na'];
const LVL_KO = { insufficient: '미흡', average: '보통', excellent: '우수', outstanding: '매우 우수', na: '해당없음' };
const LVL_EN = { insufficient: 'Insufficient', average: 'Developing', excellent: 'Proficient', outstanding: 'Leading', na: 'N/A' };
const LVL_CA = { insufficient: '不足', average: '基础', excellent: '良好', outstanding: '卓越', na: '不适用' };

// category1_question_name → 테마 key
const THEME_BY_C1 = { '수자원 관리': 'water', '수자원': 'water', '생물다양성': 'biodiversity', '생물다양성 관리': 'biodiversity' };
const THEME_SHORT = { water: '수자원', biodiversity: '생물다양성' };
const THEME_TITLE = { water: '수자원 관리', biodiversity: '생물다양성' };
const CATEGORY_ORDER = {
    water: ['정책 및 거버넌스', '리스크 분석 및 평가', '현황 측정 및 모니터링', '대응 및 실행 전략', '공급망 관리 및 공시'],
    biodiversity: ['정책 및 거버넌스', '리스크 및 기회 평가', '공급망 및 이해관계자 참여', '서식지 및 생태계 보전', '목표 설정 및 성과 관리', '모니터링 및 공시']
};

// 종합 리스크 배지 색상
// 리스크 배지 = 레벨 파스텔과 분리: 이전 saturated 색 + 흰 글자 (p3 리스크 요약 전용)
const RISK_BADGE_RGB = { HIGH: [183, 65, 56], MEDIUM: [193, 120, 42], LOW: [110, 140, 95] };

const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

export default {
    mixins: [ReportCoreMixin],
    data () {
        return {
            hyundai_colors: {
                blue: '#002c5f',
                sand: '#e4dcd3',
                light_sand: '#f6f3f2',
                gold: '#a36b4f',
                teal: '#31859c',
                teal_light: '#9ec6d2',
                gray: '#b1b1b1',
                muted: '#969696'
            },
            hyundai_colors_rgb: {
                blue: [0, 44, 95],
                sand: [228, 220, 221],
                light_sand: [246, 243, 242],
                gold: [163, 107, 79],
                teal: [49, 133, 156],
                teal_light: [158, 198, 210],
                muted: [150, 150, 150]
            },
            hyundai_logo_blue: {
                width: 33.8 * 2, height: 23.9 * 2
            }
        };
    },
    computed: {
        // core.addHeader/addFooter 가 참조
        companyInfo () {
            const info_company = this.reportData?.info?.company ?? {};
            return {
                '회사명': info_company.company_name ?? this.reportData?.info?.company_name ?? ' - '
            };
        },
        hyundai_logo_blue_png () {
            return HyundaiLogoBluePNG;
        }
    },
    methods: {
        // ============================================================
        // entry — core.generatePDF dispatch (generatePDF_hyundai_v2 미러, prior-year 제거)
        // ============================================================
        async generatePDF_hmc_theme (doc, position_top) {
            const margin = this.jsPDFOpt.margin;
            this.pageContentWidth = doc.internal.pageSize.width - (margin.left + margin.right);
            this.pageContentHeight = doc.internal.pageSize.height - (margin.top + margin.bottom);

            const data = this.htBuildData();

            // Page 1 — COVER (header/footer 없음)
            this.htPageCover(doc, data);

            // Page 2 — Summary
            doc.addPage();
            this.addHeader(doc);
            this.htPageSummary(doc, data, position_top);

            // Page 3 — 보유 리스크 요약
            this.htPageRiskSummary(doc);

            // 수자원
            this.htPageThemeResult(doc, data, 'water');
            this.htPageThemeCategory(doc, data, 'water');
            this.htPageThemeImprovement(doc, data, 'water');

            // 생물다양성
            this.htPageThemeResult(doc, data, 'biodiversity');
            this.htPageThemeCategory(doc, data, 'biodiversity');
            this.htPageThemeImprovement(doc, data, 'biodiversity');

            // Appendix
            this.htPageAppendix(doc);

            // End-of-document marker
            this.setFont(doc, 'title_sm', null, null);
            doc.setTextColor(this.hyundai_colors.muted);
            doc.text(
                '- End of Document -',
                doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 50,
                { align: 'center' }
            );

            // Footer post-pass (cover 제외)
            const totalPages = doc.getNumberOfPages();
            for (let i = 2; i <= totalPages; i++) {
                doc.setPage(i);
                this.addFooter(doc);
            }

            doc.setDocumentProperties({
                title: this.report_filename,
                subject: this.report_filename,
                author: 'QuantifiedESG Inc.',
                creator: 'QESG'
            });
            return doc;
        },

        // ============================================================
        // data transform (reportData.scoring → view model)
        // ============================================================
        htDeriveLevel (score, cap, na) {
            if (na) return 'na';
            const r = cap > 0 ? score / cap : 0;
            if (r < 0.15) return 'insufficient';
            if (r < 0.5) return 'average';
            if (r < 0.85) return 'excellent';
            return 'outstanding';
        },
        htRubricIndicator (themeKey, code) {
            return rubric?.[themeKey]?.indicators?.[code] ?? null;
        },
        htBuildData () {
            const sc = this.reportData?.scoring ?? {};
            const info = this.reportData?.info ?? {};
            const c1 = sc.category1_scoring_pd ?? [];
            const c3 = sc.category3_scoring_pd ?? [];
            const subs = sc.sub_scoring_pd ?? [];
            const total = sc.total_scoring_pd?.[0];

            const themes = {};
            c1.forEach((row) => {
                const key = THEME_BY_C1[row.category1_question_name];
                if (!key) return;
                themes[key] = {
                    key,
                    prefix: THEME_TITLE[key] || row.category1_question_name,
                    name: THEME_SHORT[key] || row.category1_question_name,
                    score_100: round1(row['환산_점수']),
                    categories: [],
                    indicators: [],
                    distribution: { insufficient: 0, average: 0, excellent: 0, outstanding: 0, na: 0 }
                };
            });
            // c1 누락 대비: 테마가 비어 있으면 기본 골격 생성
            ['water', 'biodiversity'].forEach((key) => {
                if (!themes[key]) {
                    themes[key] = {
                        key, prefix: THEME_TITLE[key], name: THEME_SHORT[key], score_100: 0,
                        categories: [], indicators: [],
                        distribution: { insufficient: 0, average: 0, excellent: 0, outstanding: 0, na: 0 }
                    };
                }
            });

            // categories (충족률 = category3 환산_점수)
            const c3map = {};
            c3.forEach((row) => {
                const key = THEME_BY_C1[row.category1_question_name];
                if (!key) return;
                (c3map[key] = c3map[key] || {})[row.category3_question_name] = round1(row['환산_점수']);
            });
            Object.keys(themes).forEach((key) => {
                const order = CATEGORY_ORDER[key] || Object.keys(c3map[key] || {});
                themes[key].categories = order
                    .filter((nm) => c3map[key] && nm in c3map[key])
                    .map((nm) => {
                        const rate = c3map[key][nm];
                        return { name: nm, rate, recommend: 70, satisfied: rate >= 70 };
                    });
            });

            // indicators
            const subNum = (subCode) => {
                const m = String(subCode).match(/TH\d\.(\d+)/);
                return m ? Number(m[1]) : 0;
            };
            subs.forEach((row) => {
                const sub = String(row.sub_code || '');
                const key = sub.startsWith('TH1') ? 'water' : sub.startsWith('TH2') ? 'biodiversity' : null;
                if (!key || !themes[key]) return;
                const cap = Number(row['배점'] ?? row.cap_score ?? 0);
                const score = Number(row.score ?? 0);
                const lvl = this.htDeriveLevel(score, cap, row.not_applicable === true);
                const code = row.label;
                const ri = this.htRubricIndicator(key, code);
                themes[key].indicators.push({
                    code,
                    sub_code: sub,
                    category: row.category3_question_name || ri?.category || '',
                    topic: this.htT(ri?.topic || row.sub_name || code),
                    question: this.htT(ri?.question || ''),
                    score,
                    cap,
                    level: lvl
                });
                themes[key].distribution[lvl] += 1;
            });
            Object.keys(themes).forEach((key) => {
                themes[key].indicators.sort((a, b) => subNum(a.sub_code) - subNum(b.sub_code));
            });

            // 전체(두 테마 합산) 경영 수준 분포
            const distributionTotal = { insufficient: 0, average: 0, excellent: 0, outstanding: 0, na: 0 };
            Object.keys(themes).forEach((key) => {
                LVL_ORDER.forEach((lv) => { distributionTotal[lv] += themes[key].distribution[lv]; });
            });

            // ── 종합 점수(총점) = 테마(category1) 환산점수의 단순 평균 ──
            // 현대차 협의(2026-07): 종합은 "(수자원 + 생물다양성) ÷ 2" 단순평균으로 산정한다.
            //   백엔드 total_scoring_pd.환산_점수 는 '전체 문항 가중'(category0)이라 값이 다름(예: 35.85) → 결과보고서는 커스텀 평균 사용.
            //   category1 에 섞여 있는 '기본'(기본문항, 환산_점수 0 등 비(非)테마) 행은 THEME_BY_C1 로 걸러 제외한다.
            //   항목별(도넛 = category1 환산점수)·분류별 충족률(category3)은 그대로 유지(변경 없음).
            const themeC1Scores = c1
                .filter((row) => THEME_BY_C1[row.category1_question_name] != null)
                .map((row) => Number(row['환산_점수']) || 0);
            const totalScore = themeC1Scores.length
                ? round1(themeC1Scores.reduce((s, v) => s + v, 0) / themeC1Scores.length)
                : round1(total?.['환산_점수']);   // 테마 category1 부재 시에만 백엔드 total 폴백

            // 회사 기본정보 (TH0.1 파서) — reportData.parser(_result).values 를 criteria_name 으로 조회 (hmc/ksteel report 패턴 미러)
            // 파싱값 0건인 회사는 백엔드가 parser_result 를 dict 가 아닌 빈 배열([])로 반환한다.
            // 이때 `[].values` 는 undefined 가 아니라 Array.prototype.values(함수)라 `??` 를 통과해버리므로
            // (예: PT. GARUDA METAL UTAMA), null 병합이 아니라 배열 여부를 명시적으로 확인해야 한다.
            const _pvRaw = this.reportData?.parser?.values ?? this.reportData?.parser_result?.values;
            const _pv = Array.isArray(_pvRaw) ? _pvRaw : [];
            const _pickCI = (name) => {
                const hit = _pv.find((v) => v.criteria_name === name);
                return (hit && Array.isArray(hit.value) && hit.value.length && String(hit.value[0] ?? '').trim() !== '')
                    ? String(hit.value[0]) : ' - ';
            };
            const _emp = _pickCI('근로자수');   // 값 있으면 단위 '명'(i18n) 부착, 누락 시 '-' 유지

            // 종합 평가 의견(기업별) — company_id 로 조회 (per-company 리스크와 동일 패턴, hmc_theme_overall.json).
            // 출처: 현대차 제공 '종합의견' 엑셀(0715). 미존재 회사는 htPageSummary 의 범용 placeholder 로 폴백.
            // 다국어: 해외(561) 기업만 번역 보유 — 영문 6사(overall_comment_ea)/중문 4사(overall_comment_ca).
            //   report_lang 로 선택하되, 번역 없으면 ko 원문(overall_comment) 폴백. 국내(560)는 ko만.
            const _ovCid = info?.company_id ?? info?.company?.company_id;
            const _ovEntry = (_ovCid != null) ? overallByCompany[String(_ovCid)] : null;
            const _overallComment = _ovEntry
                ? (this.report_lang === 'en' ? (_ovEntry.overall_comment_ea || _ovEntry.overall_comment)
                    : this.report_lang === 'ca' ? (_ovEntry.overall_comment_ca || _ovEntry.overall_comment)
                        : _ovEntry.overall_comment)
                : '';

            return {
                company: {
                    name: info.company?.company_name || info.company_name || '-',
                    code: info.vendor_code || total?.vendor_code || '-',
                    country_addr: _pickCI('소재국가'),
                    products: _pickCI('주요생산품목'),
                    employees: _emp === ' - ' ? _emp : `${_emp} ${this.htT('명')}`.trim()
                },
                period: this.htBuildPeriod(info),
                total: totalScore,
                overall_comment: _overallComment,
                distributionTotal,
                themes
            };
        },
        // 평가기간: '평가 날짜 커스텀' 체크 시 커스텀 범위, 아니면 df_nice_service_request 신청~마감
        htBuildPeriod (info) {
            let start = info?.requested_ts;
            let end = info?.duedate_ts;
            if (this.custom_duration && this.custom_start_date && this.custom_end_date) {
                start = this.custom_start_date;
                end = this.custom_end_date;
            }
            return `${this.formatDurationDate(start)} ~ ${this.formatDurationDate(end)}`;
        },

        // ============================================================
        // low-level helpers
        // ============================================================
        htLvlKO (lv) {
            const map = this.report_lang === 'en' ? LVL_EN : this.report_lang === 'ca' ? LVL_CA : LVL_KO;
            return map[lv];
        },
        // 정적 문자열 번역: KO 원문을 키로 로케일 메시지 직접 조회 (apps/hmc-theme/src/locales/*.json).
        // $t는 키의 '.'을 경로 구분자로 처리하므로 마침표 포함 문장 키는 직접 조회해야 함. 미번역/ko면 원문 폴백.
        // scope(예: 'water'/'biodiversity')가 주어지면 '{scope}::{ko}' 키를 우선 조회한다 — 동일 KO 문구가
        // 테마별로 서로 다른 공식 번역을 갖는 경우(예: 수자원/생물다양성에 재사용된 리스크 문구) 충돌 방지용.
        htT (ko, scope) {
            if (ko == null || ko === '') return ko;
            if (this.report_lang === 'ko') return ko;
            const msgs = this.$i18n ? this.$i18n.getLocaleMessage(this.report_lang) : null;
            if (!msgs) return ko;
            if (scope) {
                const scoped = msgs[`${scope}::${ko}`];
                if (scoped != null) return scoped;
            }
            return msgs[ko] != null ? msgs[ko] : ko;
        },
        // aspect-fit 이미지 (cover 전용, 자체 좌표계)
        htImg (doc, src, x, y, maxW, maxH, dims, align) {
            const [nw, nh] = dims;
            const s = Math.min(maxW / nw, maxH / nh);
            const w = nw * s, h = nh * s;
            let dx = x;
            if (align === 'r') dx = x + maxW - w;
            else if (align === 'c') dx = x + (maxW - w) / 2;
            const dy = y + (maxH - h) / 2;
            doc.addImage(src, 'PNG', dx, dy, w, h);
            return { w, h, x: dx, y: dy };
        },
        htFam () { return this.report_lang === 'ca' ? 'NotoSansHK' : 'NotoSansKR'; },
        htFont (doc, weight, size) { doc.setFont(this.htFam(), weight); doc.setFontSize(size); },
        htDonut (doc, cx, cy, r, thick, frac, label, numFS) {
            // 도넛(ring) — context2d 미사용. ring-sector 폴리곤을 doc.lines 로 채움.
            const rInner = Math.max(1, r - thick);
            const SEG = 96;
            const ringSector = (a0, a1) => {
                const pts = [];
                const n = Math.max(2, Math.round(SEG * Math.abs(a1 - a0) / (2 * Math.PI)));
                for (let i = 0; i <= n; i++) { const a = a0 + (a1 - a0) * i / n; pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]); }
                for (let i = n; i >= 0; i--) { const a = a0 + (a1 - a0) * i / n; pts.push([cx + rInner * Math.cos(a), cy + rInner * Math.sin(a)]); }
                return pts;
            };
            const fillPoly = (pts, rgb) => {
                doc.setFillColor(...rgb);
                const deltas = [];
                for (let i = 1; i < pts.length; i++) deltas.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
                doc.lines(deltas, pts[0][0], pts[0][1], [1, 1], 'F', true);
            };
            const a0 = -Math.PI / 2;
            const f = Math.max(0, Math.min(1, frac));
            fillPoly(ringSector(a0, a0 + 2 * Math.PI), [242, 244, 245]);   // track = #f2f4f5
            if (f > 0.001) fillPoly(ringSector(a0, a0 + 2 * Math.PI * f), this.hyundai_colors_rgb.blue);  // value = primary
            const fs = numFS || Math.round(r * 0.5);   // 반지름 비례 (r=36 → 18 = 기존값)
            this.setFont(doc, 'cover_company_name', null, 'bold');
            doc.setFontSize(fs);
            doc.setTextColor(0, 0, 0);   // 도넛 내부 숫자 = 검정
            doc.text(label, cx, cy + fs / 3, { align: 'center' });
        },
        // 섹션 sub-header: 굵게 + 검정 + 큰 폰트 (+ 우측 footnote 옵션)
        htSubHeading (doc, position_y, text, opt = {}) {
            const size = opt.size || 13;
            doc.setFont(this.htFam(), 'bold');
            doc.setFontSize(size);
            doc.setTextColor(0, 0, 0);
            const h = doc.getTextDimensions(text).h;
            position_y += h;
            doc.text(text, this.jsPDFOpt.margin.left, position_y);
            if (opt.footnote) {
                this.setFont(doc, 'footnote', null, null);
                doc.setTextColor(120, 120, 120);
                const fw = doc.getTextWidth(opt.footnote);
                doc.text(opt.footnote, this.jsPDFOpt.margin.left + this.pageContentWidth - fw, position_y);
            }
            if (opt.divider) {
                position_y += 5;
                doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.8);
                doc.line(this.jsPDFOpt.margin.left, position_y, this.jsPDFOpt.margin.left + this.pageContentWidth, position_y);
            }
            return position_y + (opt.gap != null ? opt.gap : 6);
        },

        // ============================================================
        // PAGE 1: COVER  (pt 기하 × 0.75 = px. 폰트 크기는 절대 pt 유지)
        // ============================================================
        htPageCover (doc, d) {
            const K = 0.75;
            const PW = doc.internal.pageSize.getWidth();   // ≈ 446.46
            const PH = doc.internal.pageSize.getHeight();  // ≈ 631.42
            const M = 34 * K;
            const CW = PW - 2 * M;

            doc.setFillColor('#002B5E'); doc.rect(0, 0, PW, PH, 'F');

            const ly = 56 * K;
            const hy = this.htImg(doc, HyundaiLogo, M, ly, 120 * K, 26 * K, DIM.hyundai, 'l');
            this.htFont(doc, 'normal', 16); doc.setTextColor('#5E7CA8');
            doc.text('·', hy.x + hy.w + 12 * K, ly + 18 * K);
            this.htImg(doc, KiaLogo, hy.x + hy.w + 22 * K, ly, 64 * K, 26 * K, DIM.kia, 'l');
            // 우측 상단: QESG White 로고 (현대·기아 로고와 같은 높이/baseline)
            this.htImg(doc, QESGLogoWhitePNG, M, ly, CW, 26 * K, DIM.qesgWhite, 'r');

            this.htFont(doc, 'bold', this.report_lang === 'ko' ? 33 : 28); doc.setTextColor('#FFFFFF');
            doc.text(this.htT('2026 현대차·기아'), M, 210 * K);
            doc.text(this.htT('공급망 ESG 테마 평가'), M, 256 * K, { maxWidth: CW });

            const pillY = 286 * K, pillH = 34 * K;
            let px = M;
            ['수자원', '생물다양성'].forEach((t) => {
                this.htFont(doc, 'bold', 13);
                const label = this.htT(t);
                const pwid = doc.getTextWidth(label) + 40 * K;
                doc.setFillColor('#203864'); doc.roundedRect(px, pillY, pwid, pillH, 6 * K, 6 * K, 'F');
                doc.setTextColor('#FFFFFF'); doc.text(label, px + pwid / 2, pillY + 22 * K, { align: 'center' });
                px += pwid + 12 * K;
            });

            this.htFont(doc, 'bold', 25); doc.setTextColor('#FFFFFF');
            doc.text(d.company.name, M, 380 * K);

            // Disclaimer: 더 하단에 위치 (하단 © 라인 제거)
            const dy = PH - 105 * K;
            this.htFont(doc, 'bold', 10); doc.setTextColor('#FFFFFF'); doc.text('Disclaimer', M, dy);
            // Disclaimer 밑줄 = 흰색
            doc.setDrawColor('#FFFFFF'); doc.setLineWidth(0.7 * K); doc.line(M, dy + 8 * K, PW - M, dy + 8 * K);
            this.htFont(doc, 'normal', 8.5); doc.setTextColor('#C7CDD6');
            doc.text(
                this.htT('본 ESG 평가 결과보고서는 각 기업으로부터 제공받은 답변 및 증빙자료를 기반으로 제3자 기관 QESG에서 평가한 서면평가 결과이며, 각 기업의 실제 현황과 일부 차이가 있을 수 있습니다.'),
                M, dy + 24 * K, { maxWidth: CW, lineHeightFactor: 1.5 }
            );
            // "평가 관련 문의사항…" 아래 줄(line) 없음
            doc.text(this.htT('평가 관련 문의사항은 QESG로 연락 주시기 바랍니다.'), M, dy + 56 * K);
        },

        // ============================================================
        // PAGE 2: SUMMARY  (clones createSummaryPage)
        // ============================================================
        htPageSummary (doc, d) {
            const margin = this.jsPDFOpt.margin;
            const leftX = margin.left;
            const CW = this.pageContentWidth;
            let position_y = this.firstElementSpacing;

            position_y = this.addTitle(doc, position_y, 1, this.htT('ESG 테마 평가 결과 요약'));

            // ── 기업 정보 ──
            position_y = this.htSubHeading(doc, position_y, this.htT('기업 정보'), { size: 14 });
            const ci = d.company;
            autoTable(doc, {
                startY: position_y,
                margin: { left: margin.left, right: margin.right },
                theme: 'grid',
                styles: { ...this.tableStyles.global, ...this.tableStyles.td, cellPadding: 4 },
                body: [
                    [
                        { content: this.htT('기업명'), styles: { ...this.tableStyles.th } }, ci.name,
                        { content: this.htT('업체 코드'), styles: { ...this.tableStyles.th } }, ci.code
                    ],
                    [
                        { content: this.htT('소재 국가 / 주소'), styles: { ...this.tableStyles.th } },
                        { content: ci.country_addr, colSpan: 3 }
                    ],
                    [
                        { content: this.htT('주요 생산 제품 및 서비스'), styles: { ...this.tableStyles.th } },
                        { content: ci.products, colSpan: 3 }
                    ],
                    [
                        { content: this.htT('근로자 수 (FY 2025)'), styles: { ...this.tableStyles.th } },
                        { content: ci.employees, colSpan: 3 }
                    ]
                ],
                columnStyles: { 0: { cellWidth: 95 }, 2: { cellWidth: 70 } }
            });
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;

            // ── 평가 정보 ──
            position_y = this.htSubHeading(doc, position_y, this.htT('평가 정보'), { size: 14 });
            autoTable(doc, {
                startY: position_y,
                margin: { left: margin.left, right: margin.right },
                theme: 'grid',
                styles: { ...this.tableStyles.global, ...this.tableStyles.td, cellPadding: 4 },
                body: [[{ content: this.htT('평가기간'), styles: { ...this.tableStyles.th } }, this.htT(d.period)]],
                columnStyles: { 0: { cellWidth: 95 } }
            });
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;

            // ── 평가 결과 ── (상위 타이틀, 하단 라인)
            position_y = this.htSubHeading(doc, position_y, this.htT('평가 결과'), { size: 14, divider: true });

            // ESG 테마 평가 총점 (하위 타이틀, 설명 배너 없음 → 바로 총점)
            position_y = this.htSubHeading(doc, position_y, this.htT('ESG 테마 평가 총점'), { size: 11.5 });

            // 총점 박스 (th 색): Total Score + 큰 숫자 + 분절 막대 + 0~100 scale + marker
            // height 74→54 (-20, 2단계: -12 후 추가 -8): 종합의견 공간 확보. 점수 폰트·바 높이·위치 축소 비례 조정.
            const boxH = 54;
            doc.setFillColor(246, 243, 242);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setLineWidth(this.jsPDFOpt.card.lineWidth);
            doc.roundedRect(leftX, position_y, CW, boxH, 3, 3, 'FD');
            const padX = leftX + 18;
            this.setFont(doc, 'footnote', null, 'medium');
            doc.setFontSize(10);
            doc.setTextColor(77, 77, 77);
            doc.text('Total Score', padX, position_y + 15);
            this.setFont(doc, 'cover_company_name', null, 'bold');
            doc.setFontSize(24);
            doc.setTextColor(0, 0, 0);   // 점수 = 검정
            const _scoreUnit = this.report_lang === 'en' ? '' : this.htT(' 점');   // en은 'Points' 폭 넘쳐 단위 생략
            doc.text(`${d.total.toFixed(1)}${_scoreUnit}`, padX, position_y + 40);
            const barX = leftX + 150, barW = CW - 150 - 20, barY = position_y + 22, barH = 12, br = barH / 2;
            doc.setFillColor(228, 220, 211);
            doc.roundedRect(barX, barY, barW, barH, br, br, 'F');
            const frac = Math.max(0, Math.min(1, d.total / 100));
            if (frac > 0) {
                doc.setFillColor(...this.hyundai_colors_rgb.blue);
                doc.roundedRect(barX, barY, Math.max(barH, barW * frac), barH, br, br, 'F');
            }
            const mx = barX + barW * frac;
            doc.setFillColor(17, 17, 17);
            doc.triangle(mx - 5, barY - 10, mx + 5, barY - 10, mx, barY - 2, 'F');
            for (let i = 0; i <= 10; i++) {
                const t = i * 10; const tx = barX + barW * (t / 100);
                const emph = (t === 0 || t === 100);
                this.setFont(doc, 'footnote', null, emph ? 'bold' : 'normal');
                doc.setFontSize(emph ? 10 : 8);
                doc.setTextColor(emph ? 26 : 89, emph ? 26 : 89, emph ? 26 : 89);
                doc.text(String(t), tx, barY + barH + 11, { align: 'center' });
            }
            position_y += boxH + this.jsPDFOpt.spacing.section_xs;

            // ── 영역별 결과 (도넛) ── (하위 타이틀)
            position_y = this.htSubHeading(doc, position_y, this.htT('ESG 테마 평가 영역별 결과'), {
                size: 11.5,
                footnote: this.htT('(각 영역별 점수는 100점 만점 기준 환산 값)')
            });
            // 헤더: '영역별 점수' 밴드 제거 → 수자원 / 생물다양성 2분할 헤더 (th 색)
            const half = CW / 2;
            const hdrH = 18;
            const headerCells = [
                { x: leftX, name: '수자원' },
                { x: leftX + half, name: '생물다양성' }
            ];
            headerCells.forEach((h) => {
                doc.setFillColor(246, 243, 242);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.setLineWidth(0.5);
                doc.rect(h.x, position_y, half, hdrH, 'FD');
                this.setFont(doc, 'title_xs', null, 'bold');
                doc.setTextColor(41, 40, 40);
                doc.text(this.htT(h.name), h.x + half / 2, position_y + hdrH / 2 + 1, { align: 'center', baseline: 'middle' });
            });
            // 도넛 행: 각 셀 정중앙 (80→70 축소 후 +4 복원 = 74, 반지름 30→32. 의견 container는 position_y 스레딩으로 자동 -4)
            // en 은 종합의견이 길어(최장 1186자, PT. GARUDA METAL UTAMA) 의견 박스가 1줄 넘침 → 도넛행에서 12px 양보.
            const isEnSummary = this.report_lang === 'en';
            const rowY = position_y + hdrH, rowH = isEnSummary ? 62 : 74;
            const donutR = isEnSummary ? 28 : 32, donutTh = 9;
            const areaCells = [
                { x: leftX, val: d.themes.water?.score_100 ?? 0 },
                { x: leftX + half, val: d.themes.biodiversity?.score_100 ?? 0 }
            ];
            areaCells.forEach((cell) => {
                doc.setFillColor(255, 255, 255);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.setLineWidth(0.5);
                doc.rect(cell.x, rowY, half, rowH, 'S');
                this.htDonut(doc, cell.x + half / 2, rowY + rowH / 2, donutR, donutTh, cell.val / 100, cell.val.toFixed(1));
            });
            position_y = rowY + rowH + this.jsPDFOpt.spacing.section_xs;

            // ── 종합 평가 의견 (제목 + 코멘트 박스 — 텍스트 높이에 맞춰 박스 sizing, 상하 패딩 균등) ──
            position_y = this.htSubHeading(doc, position_y, this.htT('종합 평가 의견'), { size: 11.5 });
            // d.overall_comment 연동 전 기본 placeholder (회사별 사실을 지어내지 않는 범용 안내문 — 약 3줄).
            const OVERALL_COMMENT_PLACEHOLDER = '본 보고서는 수자원·생물다양성 영역의 ESG 관리 수준을 분류별로 진단한 결과입니다. 영역별 점수와 권고 수준 충족 여부를 검토하여 강점과 보완이 필요한 영역을 확인하시기 바랍니다. 세부 개선 방향은 분류별 개선 과제를 참고하여 단계적으로 추진하실 것을 권장합니다.';
            const overallComment = String(d.overall_comment || OVERALL_COMMENT_PLACEHOLDER).trim();
            const padV = 8;   // 내부 상단 패딩 10→8 (상단 여백 축소)
            const padH = 10;
            const OPINION_FONT = this.report_lang === 'en' ? 8 : 10;   // 종합의견 하단 잘림 방지: ko·zh 10, en 8
            const OPINION_LH = 1.15;   // 줄간격(배수)
            // title_xs 대신 본문 normal 폰트를 직접 세팅 (type별 size/weight 간섭 제거)
            doc.setFont(this.htFam(), 'normal');
            doc.setFontSize(OPINION_FONT);
            const cLines = doc.splitTextToSize(this.htT(overallComment), CW - padH * 2);
            // 줄높이는 fontSize×lineHeight로 직접 계산 (getTextDimensions가 active lineHeightFactor를 무시하는 이슈 회피)
            const lineH = OPINION_FONT * OPINION_LH;
            const textBlockH = cLines.length * lineH;
            // 박스 높이: 텍스트 + 상단 패딩(padV) + 하단 여백(opinionBottomPad). 푸터 초과 시 클램프.
            const pageH = doc.internal.pageSize.height;
            // 푸터 위 여백. en 은 종합의견 최장문(1186자)이 클램프에 걸려 마지막 줄이 테두리에 닿아 → 8px 양보.
            const bottomLimit = pageH - margin.bottom - (this.report_lang === 'en' ? 2 : 10);
            const opinionBottomPad = this.report_lang === 'en' ? 15 : 25;   // en은 텍스트 길어 하단여백 축소
            const opinionH = Math.min(textBlockH + padV + opinionBottomPad, bottomLimit - position_y);
            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setLineWidth(0.5);
            doc.roundedRect(leftX, position_y, CW, opinionH, 3, 3, 'FD');
            doc.setTextColor(51, 51, 51);
            cLines.forEach((ln, i) => {
                doc.text(ln, leftX + padH, position_y + padV + lineH * (i + 0.5), { baseline: 'middle' });
            });
            position_y += opinionH;

            return position_y;
        },

        // ============================================================
        // PAGE 3: 보유 리스크 요약  (정적 placeholder)
        // ============================================================
        htPageRiskSummary (doc) {
            doc.addPage();
            this.addHeader(doc);
            const margin = this.jsPDFOpt.margin;
            const leftX = margin.left;
            const CW = this.pageContentWidth;
            let position_y = this.firstElementSpacing;

            position_y = this.addTitle(doc, position_y, 1, this.htT('수자원·생물다양성 보유 리스크 요약'));

            // per-company 리스크 (company_id 조회, 미존재 시 static fallback)
            const _cid = this.reportData?.info?.company_id;
            const rs = (_cid != null && riskByCompany[String(_cid)]) ? riskByCompany[String(_cid)] : riskSummary;

            // 종합 리스크 보유 수준
            position_y = this.htSubHeading(doc, position_y, this.htT('종합 리스크 보유 수준'), { size: 14 });
            const ov = rs.overall;
            // 배지 (레벨 정규화: MID→MEDIUM)
            const _ovKey = { HIGH: 'HIGH', MID: 'MEDIUM', MEDIUM: 'MEDIUM', LOW: 'LOW' }[String(ov.level).toUpperCase()] || 'MEDIUM';
            const badgeRGB = RISK_BADGE_RGB[_ovKey] || RISK_BADGE_RGB.MEDIUM;
            const badgeW = 92, badgeH = 38;
            // 설명 라인 먼저 계산 → 박스 높이 동적 산정 (고정 60이면 영문 장문 시 텍스트가 박스 밖으로 오버플로우).
            //   짧은 글은 최소 60 유지(배지 여백 확보) → 단문에서도 어색하지 않게.
            this.setFont(doc, 'title_xs', null, null);
            doc.setFontSize(10.5);
            const ovDesc = doc.splitTextToSize(this.htT(ov.desc), CW - badgeW - 42);
            // 줄간격: 이 문서는 px 단위인데 setFontSize 는 pt 기준 → jsPDF 배열 렌더의 실제 줄간격은
            //   fontSize * lineHeightFactor / scaleFactor(96/72) = 10.5*1.5/1.333 = 11.8125px.
            //   줄별 렌더로 원본과 같은 줄간격을 내려면 같은 값을 써야 한다 (10.5*1.5 로 하면 33% 더 벌어짐).
            const ovLH = 10.5 * 1.5 / (96 / 72);
            const ovPadV = 11;   // 상단/하단 패딩 (배지·텍스트 공통 top 정렬 기준)
            const ovTextH = ovDesc.length * ovLH;
            // 최소 60 (원본값. 배지 상단11+38=49 수용). 그 이상은 텍스트 높이 기준 → 상/하단 패딩 각 11 로 대칭.
            const ovH = Math.max(60, ovPadV * 2 + ovTextH);
            doc.setFillColor(...this.hyundai_colors_rgb.light_sand);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setLineWidth(0.5);
            doc.rect(leftX, position_y, CW, ovH, 'FD');
            // 배지 (상단 정렬 — 설명 텍스트 top 과 동일 padding)
            const badgeX = leftX + CW - badgeW - 14, badgeY = position_y + ovPadV;
            doc.setFillColor(...badgeRGB);
            doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 5, 5, 'F');
            this.setFont(doc, 'cover_company_name', null, 'bold');
            doc.setFontSize(18);
            doc.setTextColor(255, 255, 255);
            doc.text(_ovKey, badgeX + badgeW / 2, badgeY + badgeH / 2 + 1, { align: 'center', baseline: 'middle' });
            // 설명 — 폰트/줄간격 원본 그대로 (title_xs 10.5, 줄간격 ovLH = 10.5*1.5).
            //   단 doc.text(배열, baseline:'middle') 은 블록을 이중정렬해 위로 치우침 → 하단 여백이 과다해짐.
            //   → 줄별로 y 를 직접 지정해 렌더 (블록 = [ovPadV, ovPadV+ovTextH] → 상/하단 패딩 각 ovPadV 로 대칭).
            this.setFont(doc, 'title_xs', null, null);
            doc.setFontSize(10.5);
            doc.setTextColor(51, 51, 51);
            ovDesc.forEach((ln, i) => {
                doc.text(ln, leftX + 14, position_y + ovPadV + ovLH * (i + 0.5), { baseline: 'middle' });
            });
            position_y += ovH + this.jsPDFOpt.spacing.section_md;

            // 영역별 리스크 보유 수준 (2열)
            position_y = this.htSubHeading(doc, position_y, this.htT('영역별 리스크 보유 수준'), { size: 14 });
            const colGap = 16;
            const colW = (CW - colGap) / 2;
            const startY = position_y;
            const colX = [leftX, leftX + colW + colGap];
            let maxBottom = startY;
            ['water', 'biodiversity'].forEach((key, ci) => {
                let cy = startY;
                const x = colX[ci];
                // 테마 그룹 헤더 바 (p2 테이블 th 와 동일 색 — 통일). 계층: 영역별 리스크 > 테마 > 행
                const thH = 25;
                doc.setFillColor(246, 243, 242);
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.setLineWidth(0.4);
                doc.rect(x, cy, colW, thH, 'FD');
                this.setFont(doc, 'title_xs', null, 'bold');
                doc.setFontSize(11);
                doc.setTextColor(41, 40, 40);
                doc.text(this.htT(THEME_SHORT[key]), x + colW / 2, cy + thH / 2 + 1, { align: 'center', baseline: 'middle' });
                cy += thH;
                const labW = 58, lvW = 56;
                const descX = x + labW + lvW;
                const descW = colW - labW - lvW;
                const isEn = this.report_lang === 'en';
                (rs[key] || []).forEach((row) => {
                    const rowH = isEn ? 46 : 38;   // desc 3줄 여유 확보 (en은 길어 +4)
                    const lvKey = String(row.level).toUpperCase();
                    const isNa = lvKey === 'NA';
                    // 라벨 셀 (th 회색) — 항상
                    doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                    doc.setLineWidth(0.4);
                    doc.setFillColor(246, 243, 242);
                    doc.rect(x, cy, labW, rowH, 'FD');
                    this.setFont(doc, 'title_xs', null, 'medium');
                    doc.setFontSize(10);
                    doc.setTextColor(41, 40, 40);
                    doc.text(doc.splitTextToSize(this.htT(row.label), labW - 6), x + labW / 2, cy + rowH / 2 + 1, { align: 'center', baseline: 'middle' });

                    if (isNa) {
                        // NA: 수준+설명 셀 병합 → 회색 '해당없음'(en 'N/A' / zh '不适用') 하나만 중앙
                        doc.setFillColor(255, 255, 255);
                        doc.rect(x + labW, cy, lvW + descW, rowH, 'FD');
                        this.setFont(doc, 'footnote', null, null);
                        doc.setFontSize(9);
                        doc.setTextColor(150, 150, 150);
                        doc.text(this.htT('해당없음'), x + labW + (lvW + descW) / 2, cy + rowH / 2 + 1, { align: 'center', baseline: 'middle' });
                        cy += rowH;
                        return;
                    }

                    // 값 셀 + 설명 셀 (흰색)
                    doc.setFillColor(255, 255, 255);
                    doc.rect(x + labW, cy, lvW, rowH, 'FD');
                    doc.rect(descX, cy, descW, rowH, 'FD');
                    // level chip (pill)
                    const lvRGB = RISK_BADGE_RGB[lvKey] || RISK_BADGE_RGB.LOW;
                    const chipH = 19, chipW = lvW - 10;
                    const chipX = x + labW + (lvW - chipW) / 2, chipY = cy + (rowH - chipH) / 2;
                    doc.setFillColor(...lvRGB);
                    doc.roundedRect(chipX, chipY, chipW, chipH, 3, 3, 'F');
                    this.setFont(doc, 'title_xs', null, 'bold');
                    doc.setFontSize(9.5);
                    doc.setTextColor(255, 255, 255);
                    doc.text(row.level, x + labW + lvW / 2, cy + rowH / 2 + 1, { align: 'center', baseline: 'middle' });
                    // desc (4줄↑이면 폰트 0.5씩 축소 → 셀 수직 중앙)
                    this.setFont(doc, 'footnote', null, null);
                    const descLHF = isEn ? 1.25 : 1.35, descMinFS = 7;
                    let descFS = isEn ? 8 : 9;
                    doc.setFontSize(descFS);
                    let dLines = doc.splitTextToSize(this.htT(row.desc), descW - 12);
                    while (dLines.length > 3 && descFS > descMinFS) {
                        descFS -= 0.5;
                        doc.setFontSize(descFS);
                        dLines = doc.splitTextToSize(this.htT(row.desc), descW - 12);
                    }
                    doc.setTextColor(60, 60, 60);
                    // 각 줄을 개별 baseline:middle 로 그림 → 셀 수직 중앙 (배열+middle 이중정렬 버그 회피)
                    const descLH = descFS * descLHF;
                    const descStartY = cy + rowH / 2 - (dLines.length - 1) * descLH / 2;
                    dLines.forEach((ln, li) => {
                        doc.text(ln, descX + 7, descStartY + li * descLH, { baseline: 'middle' });
                    });
                    cy += rowH;
                });
                // 그룹 외곽 테두리 (헤더+행 묶기) — 연한 테두리
                doc.setDrawColor(this.jsPDFOpt.card.borderColor);
                doc.setLineWidth(0.5);
                doc.rect(x, startY, colW, cy - startY, 'S');
                if (cy > maxBottom) maxBottom = cy;
            });
            position_y = maxBottom + 8;

            // note
            this.setFont(doc, 'footnote', null, null);
            doc.setFontSize(8.5);
            doc.setTextColor(this.hyundai_colors.muted);
            doc.text(this.htT(riskSummary.note), leftX, position_y);
            position_y += 12;

            return position_y;
        },

        // ============================================================
        // PAGE: {테마} 평가 결과  (clones createCorePage_v2 + summary level cards)
        // ============================================================
        htPageThemeResult (doc, d, key) {
            const th = d.themes[key];
            doc.addPage();
            this.addHeader(doc);
            const margin = this.jsPDFOpt.margin;
            const leftX = margin.left;
            const isEn = this.report_lang === 'en';   // en은 텍스트 길어 폰트/여백 축소
            let position_y = this.firstElementSpacing;

            position_y = this.addTitle(doc, position_y, 1, this.htT(`${th.prefix} 평가 결과`));

            // ── 영역 총점 + 답변별 경영 수준 (좌우 2단, PPTX 구성) ──
            const scoreCardW = 120;
            const tableX = leftX + scoreCardW + 14;
            // 헤더 (좌우)
            this.setFont(doc, 'title_xs', null, 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            const headY = position_y + doc.getTextDimensions('가').h;
            doc.text(this.htT('영역 총점'), leftX, headY);
            doc.text(this.htT('답변별 경영 수준'), tableX, headY);
            const blockTop = headY + 8;

            // 답변별 경영 수준 표 (먼저 그려 높이 측정)
            const dist = th.distribution;
            const totalQ = LVL_ORDER.reduce((s, lv) => s + (dist[lv] || 0), 0);
            const levelBody = LVL_ORDER.map((lv) => {
                let [lab, desc] = levelMeta[lv] || ['', ''];
                if (lv === 'na') { lab = lab || '평가 대상 외'; desc = desc || '해당사항 없음 (평가 제외)'; }
                return [
                    {
                        content: this.htLvlKO(lv),
                        styles: {
                            fillColor: LEVEL_ACCENT_RGB[lv], textColor: LEVEL_TEXT_RGB[lv],
                            fontStyle: 'bold', halign: 'center'
                        }
                    },
                    { content: this.htT(lab), styles: { fontStyle: 'medium' } },
                    this.htT(desc),
                    { content: `${dist[lv]}${this.htT('개')}`, styles: { halign: 'center', fontStyle: 'medium' } }
                ];
            });
            autoTable(doc, {
                startY: blockTop,
                margin: { left: tableX, right: margin.right },
                theme: 'grid',
                showHead: 'everyPage',
                showFoot: 'lastPage',
                head: [[
                    { content: this.htT('수준'), styles: { halign: 'center' } },
                    { content: this.htT('관리 상태'), styles: { halign: 'center' } },
                    { content: this.htT('설명'), styles: { halign: 'center' } },
                    { content: this.htT('문항 수'), styles: { halign: 'center' } }
                ]],
                foot: [[
                    { content: this.htT('합계'), colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
                    { content: `${totalQ}${this.htT('개')}`, styles: { halign: 'center', fontStyle: 'bold' } }
                ]],
                headStyles: { ...this.tableStyles.th, fontSize: isEn ? 7.5 : 8.5 },
                footStyles: { ...this.tableStyles.th, fontSize: isEn ? 7.5 : 8.5 },
                styles: { ...this.tableStyles.global, ...this.tableStyles.td, fontSize: isEn ? 7 : 8.5, cellPadding: isEn ? 3 : 4, minCellHeight: isEn ? 20 : 24, valign: 'middle' },
                body: levelBody,
                columnStyles: { 0: { cellWidth: 46, fontStyle: 'bold' }, 1: { cellWidth: 64, fontStyle: 'medium', halign: 'center' }, 3: { cellWidth: 38, halign: 'center' } }
            });
            const tableBottom = doc.lastAutoTable.finalY;

            // 영역 총점 박스 (표 높이에 맞춤: 점수 + /100 척도 + 미니 0-100 바)
            const scoreCardH = tableBottom - blockTop;
            doc.setFillColor(this.hyundai_colors.light_sand);
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setLineWidth(0.5);
            doc.roundedRect(leftX, blockTop, scoreCardW, scoreCardH, 6, 6, 'FD');

            const boxCx = leftX + scoreCardW / 2;
            const boxPad = 16;
            const groupH = 58;
            const gTop = blockTop + Math.max(boxPad, (scoreCardH - groupH) / 2);
            // 점수 + 척도 한 줄: "30.6 / 100점" (같은 baseline, 그룹 중앙정렬)
            const scoreStr = th.score_100.toFixed(1);
            const scoreSuffix = this.htT(' / 100점');
            this.setFont(doc, 'cover_company_name', null, 'bold');
            doc.setFontSize(30);
            const scoreW = doc.getTextWidth(scoreStr);
            this.setFont(doc, 'footnote', null, 'medium');
            doc.setFontSize(11);
            const suffixW = doc.getTextWidth(scoreSuffix);
            const lineStartX = boxCx - (scoreW + suffixW) / 2;
            const lineBaseY = gTop + 22;
            this.setFont(doc, 'cover_company_name', null, 'bold');
            doc.setFontSize(30);
            doc.setTextColor(0, 0, 0);
            doc.text(scoreStr, lineStartX, lineBaseY, { baseline: 'alphabetic' });
            this.setFont(doc, 'footnote', null, 'medium');
            doc.setFontSize(11);
            doc.setTextColor(120, 120, 120);
            doc.text(scoreSuffix, lineStartX + scoreW, lineBaseY, { baseline: 'alphabetic' });
            // 미니 0-100 바
            const sbarW = scoreCardW - boxPad * 2;
            const sbarX = leftX + boxPad;
            const sbarY = gTop + 38;
            const sbarH = 6;
            doc.setFillColor(...this.hyundai_colors_rgb.sand);
            doc.roundedRect(sbarX, sbarY, sbarW, sbarH, 3, 3, 'F');
            const sFrac = Math.max(0, Math.min(1, th.score_100 / 100));
            if (sFrac > 0) {
                doc.setFillColor(...this.hyundai_colors_rgb.blue);
                doc.roundedRect(sbarX, sbarY, Math.max(sbarH, sbarW * sFrac), sbarH, 3, 3, 'F');
            }
            this.setFont(doc, 'footnote', null, 'normal');
            doc.setFontSize(7);
            doc.setTextColor(150, 150, 150);
            doc.text('0', sbarX, sbarY + sbarH + 8);
            doc.text('100', sbarX + sbarW, sbarY + sbarH + 8, { align: 'right' });

            position_y = tableBottom + this.jsPDFOpt.spacing.section_sm;

            // ── 문항별 평가 결과 ──
            position_y = this.htSubHeading(doc, position_y, this.htT('문항별 평가 결과'), { size: 12 });
            // 분류(category) 그룹핑 → rowSpan 병합
            const catGroups = [];
            th.indicators.forEach((it) => {
                let g = catGroups.find((x) => x.cat === it.category);
                if (!g) { g = { cat: it.category, items: [] }; catGroups.push(g); }
                g.items.push(it);
            });
            const rows = [];
            catGroups.forEach((g) => {
                g.items.forEach((it, idx) => {
                    const lv = it.level;
                    const row = [];
                    if (idx === 0) {
                        row.push({
                            content: this.htT(g.cat), rowSpan: g.items.length,
                            styles: { fillColor: [246, 243, 242], textColor: [41, 40, 40], fontStyle: 'bold', valign: 'middle', halign: 'center' }
                        });
                    }
                    row.push({ content: it.code, styles: { halign: 'center' } });
                    row.push(it.topic);
                    row.push({
                        content: this.htLvlKO(lv),
                        styles: { fillColor: LEVEL_ACCENT_RGB[lv], textColor: LEVEL_TEXT_RGB[lv], fontStyle: 'bold', halign: 'center' }
                    });
                    rows.push(row);
                });
            });
            autoTable(doc, {
                startY: position_y,
                margin: { left: leftX, right: margin.right, top: this.firstElementSpacing },   // 연속페이지 헤더 충돌 방지
                theme: 'grid',
                showHead: 'everyPage',
                pageBreak: 'auto',
                rowPageBreak: 'avoid',
                head: [[this.htT('분류'), this.htT('문항번호'), this.htT('문항'), this.htT('답변')]],
                headStyles: { ...this.tableStyles.th, fontSize: 9.5, halign: 'center' },
                styles: { ...this.tableStyles.global, ...this.tableStyles.td, fontSize: isEn ? 8 : 8.5, cellPadding: 4 },
                body: rows,
                columnStyles: {
                    0: { cellWidth: 92 },
                    1: { cellWidth: 50, halign: 'center' },
                    3: { cellWidth: 56 }
                },
                didDrawPage: (h) => this.addHeader(h.doc)
            });
            return doc.lastAutoTable.finalY;
        },

        // ============================================================
        // PAGE: {테마} 분류별 평가 결과  (bars vs 권고70 + 충족여부 table)
        // ============================================================
        htPageThemeCategory (doc, d, key) {
            const th = d.themes[key];
            doc.addPage();
            this.addHeader(doc);
            const margin = this.jsPDFOpt.margin;
            const leftX = margin.left;
            const CW = this.pageContentWidth;
            let position_y = this.firstElementSpacing;

            position_y = this.addTitle(doc, position_y, 1, this.htT(`${th.prefix} 분류별 평가 결과`));

            // ── 분류별 충족률 (세로 막대 차트 + 가로 그리드 + 권고 70 임계선) ──
            position_y = this.htSubHeading(doc, position_y, this.htT('분류별 충족률'), { size: 12 });

            // 막대 색상: 권고 충족=blue, 미달(<70)=red(brick)
            const MET_RGB = this.hyundai_colors_rgb.blue;
            const UNDER_RGB = [183, 65, 56];
            const REC_LINE_RGB = this.hyundai_colors_rgb.teal;   // 권고 수준(70) 임계선
            const MIN_LINE_RGB = [230, 92, 52];                  // 최소 충족 수준(30) — 빨강 계통, 막대(brick)와 구분되는 밝은 주황빛 적색

            // legend (우측 정렬): 충족 / 미달 / 권고 수준(dash) / 최소 충족 수준(dash)
            const legBaseY = position_y + doc.getTextDimensions('가').h;
            const drawDashSwatch = (rgb, x) => {
                doc.setDrawColor(...rgb);
                doc.setLineWidth(0.9);
                doc.setLineDashPattern([2.5, 2], 0);
                doc.line(x, legBaseY - 3, x + 16, legBaseY - 3);
                doc.setLineDashPattern([], 0);
            };
            this.setFont(doc, 'footnote', null, 'medium');
            doc.setFontSize(8.5);
            const lgA = this.htT('충족');
            const lgB = this.htT('미달');
            const lgC = this.htT('권고 수준');
            const lgD = this.htT('최소 충족 수준');
            const wA = 9 + 4 + doc.getTextWidth(lgA);
            const wB = 9 + 4 + doc.getTextWidth(lgB);
            const wC = 16 + 4 + doc.getTextWidth(lgC);
            const wD = 16 + 4 + doc.getTextWidth(lgD);
            const legGap = 12;
            let legX = leftX + CW - (wA + wB + wC + wD + legGap * 3);
            // 충족 (blue square)
            doc.setFillColor(...MET_RGB);
            doc.rect(legX, legBaseY - 7, 9, 9, 'F');
            doc.setTextColor(64, 64, 64);
            doc.text(lgA, legX + 13, legBaseY);
            legX += wA + legGap;
            // 미달 (brick-red square)
            doc.setFillColor(...UNDER_RGB);
            doc.rect(legX, legBaseY - 7, 9, 9, 'F');
            doc.setTextColor(64, 64, 64);
            doc.text(lgB, legX + 13, legBaseY);
            legX += wB + legGap;
            // 권고 수준 (teal dash)
            drawDashSwatch(REC_LINE_RGB, legX);
            doc.setTextColor(64, 64, 64);
            doc.text(lgC, legX + 20, legBaseY);
            legX += wC + legGap;
            // 최소 충족 수준 (red-family dash)
            drawDashSwatch(MIN_LINE_RGB, legX);
            doc.setTextColor(64, 64, 64);
            doc.text(lgD, legX + 20, legBaseY);
            position_y = legBaseY + 14;

            // 차트 영역 (y축 숫자 미표기 → 전폭 사용)
            const cats = th.categories;
            const plotX = leftX;
            const plotW = CW;
            const chartH = this.report_lang === 'en' ? 130 : 142;   // en은 텍스트 길어 페이지 빡빡 → 차트 height 축소(-12)
            const labelArea = this.report_lang === 'en' ? 48 : 40;
            const plotTop = position_y;
            const plotBottom = plotTop + chartH;
            const ticks = [0, 20, 40, 60, 80, 100];

            // 가로 그리드라인 (y축 눈금 라벨 없음)
            ticks.forEach((v) => {
                const gy = plotBottom - (v / 100) * chartH;
                doc.setDrawColor(v === 0 ? 180 : 228, v === 0 ? 180 : 228, v === 0 ? 180 : 228);
                doc.setLineWidth(v === 0 ? 0.5 : 0.3);
                doc.line(plotX, gy, plotX + plotW, gy);
            });

            // 세로 막대 + 분류명
            const n = Math.max(1, cats.length);
            const slotW = plotW / n;
            const barW = Math.min(36, slotW * 0.46);
            cats.forEach((c, i) => {
                const cxc = plotX + i * slotW + slotW / 2;
                const h = (Math.max(0, Math.min(100, c.rate)) / 100) * chartH;
                doc.setFillColor(...(c.rate < 70 ? UNDER_RGB : MET_RGB));
                doc.rect(cxc - barW / 2, plotBottom - h, barW, h, 'F');
                this.setFont(doc, 'footnote', null, 'medium');
                doc.setFontSize(this.report_lang === 'en' ? 8 : 10);
                doc.setTextColor(64, 64, 64);
                const nm = doc.splitTextToSize(this.htT(c.name), slotW - 4).slice(0, this.report_lang === 'en' ? 3 : 2);
                doc.text(nm, cxc, plotBottom + 13, { align: 'center' });
            });

            // 임계선 오버레이 (막대 위): 권고 수준(70, teal) + 최소 충족 수준(30, red 계통)
            doc.setLineWidth(0.9);
            doc.setLineDashPattern([2.5, 2], 0);
            const recY = plotBottom - (70 / 100) * chartH;
            doc.setDrawColor(...REC_LINE_RGB);
            doc.line(plotX, recY, plotX + plotW, recY);
            const minY = plotBottom - (30 / 100) * chartH;
            doc.setDrawColor(...MIN_LINE_RGB);
            doc.line(plotX, minY, plotX + plotW, minY);
            doc.setLineDashPattern([], 0);

            position_y = plotBottom + labelArea + this.jsPDFOpt.spacing.section_xs - 30;

            // ── 임계선 설명 (muted, 테두리 박스 · 배경 없음) ──
            const threshNotes = [
                ['최소 충족 수준', '기초적인 요건은 충족하였으나 주의 및 개선이 필요한 보통 단계입니다. 결과 보고서의 피드백을 바탕으로 단기적인 보완 계획을 수립하고 실행해야 합니다.'],
                ['권고 수준', '안정 궤도에 오른 우수 및 권장 단계입니다. 현재의 프로세스나 방식을 유지하며, 나아가 고도화된 성과를 위해 자율적인 발전 과제를 추진하기에 적합합니다.']
            ];
            const notePadX = 12, notePadV = 10, noteFS = 8.5, noteLH = 1.45, noteGap = 5;
            const notePadVBottom = notePadV - 4; // 하단 패딩만 살짝 줄임
            // desc x 정렬: 가장 긴 라벨('최소 충족 수준') 기준으로 label width 통일
            this.setFont(doc, 'footnote', null, 'bold');
            doc.setFontSize(noteFS);
            const noteLabelW = Math.max(...threshNotes.map(([k]) => doc.getTextWidth(`• ${this.htT(k)} :`))) + 10;
            const noteDescW = CW - notePadX * 2 - noteLabelW;
            // 각 항목 wrapped lines·높이 사전 계산 → 박스 높이 산정
            doc.setLineHeightFactor(noteLH);
            const noteRows = threshNotes.map(([k, v]) => {
                const lines = doc.splitTextToSize(this.htT(v), noteDescW);
                return { k, lines, h: doc.getTextDimensions(lines, { lineHeightFactor: noteLH }).h };
            });
            const noteBoxH = noteRows.reduce((s, r) => s + r.h, 0) + noteGap * (noteRows.length - 1) + notePadV + notePadVBottom;
            // 테두리만 (bg 없음)
            doc.setDrawColor(this.jsPDFOpt.card.borderColor);
            doc.setLineWidth(0.5);
            doc.roundedRect(leftX, position_y, CW, noteBoxH, 3, 3, 'S');
            // 내부 텍스트 (라벨 bold + desc normal, x 정렬)
            let noteY = position_y + notePadV;
            noteRows.forEach((r) => {
                this.setFont(doc, 'footnote', null, 'bold');
                doc.setFontSize(noteFS);
                doc.setTextColor(120, 120, 120);
                doc.text(`• ${this.htT(r.k)} :`, leftX + notePadX, noteY, { baseline: 'top' });
                this.setFont(doc, 'footnote', null, null);
                doc.setFontSize(noteFS);
                doc.setTextColor(120, 120, 120);
                doc.text(r.lines, leftX + notePadX + noteLabelW, noteY, { baseline: 'top' });
                noteY += r.h + noteGap;
            });
            doc.setLineHeightFactor(1.15);
            position_y += noteBoxH + this.jsPDFOpt.spacing.section_xs;

            // ── 분류별 권고 수준 충족 여부 ──
            position_y = this.htSubHeading(doc, position_y, this.htT('분류별 권고 수준 충족 여부'), { size: 12 });
            const body = th.categories.map((c) => {
                const failing = th.indicators
                    .filter((it) => it.category === c.name && (it.level === 'insufficient' || it.level === 'average'))
                    .map((it) => `[${it.code}] ${it.topic}`)
                    .join('\n');
                return [
                    { content: this.htT(c.name), styles: { halign: 'center', fontStyle: 'medium' } },
                    { content: c.satisfied ? '충족' : '미충족', styles: { halign: 'center' } },
                    c.satisfied ? '-' : failing
                ];
            });
            autoTable(doc, {
                startY: position_y,
                margin: { left: leftX, right: margin.right },
                theme: 'grid',
                showHead: 'everyPage',
                pageBreak: 'auto',
                rowPageBreak: 'avoid',
                head: [[this.htT('분류'), this.htT('권고 수준 충족 여부'), this.htT('개선 권고')]],
                headStyles: { ...this.tableStyles.th, fontSize: 9.5, halign: 'center' },
                styles: { ...this.tableStyles.global, ...this.tableStyles.td, fontSize: 8.5, cellPadding: 4 },
                body,
                columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 96, halign: 'center', valign: 'middle' } },
                // 미충족 행 = 연한 레드 tint (전체 3셀). fill은 didDrawCell 이전에 칠해져야 함.
                didParseCell: (data) => {
                    if (data.section === 'body') {
                        const cc = th.categories[data.row.index];
                        if (cc && !cc.satisfied) data.cell.styles.fillColor = [254, 240, 240];
                    }
                },
                // 충족/미충족 = 박스 대신 상태 점(dot) + 컬러 라벨
                willDrawCell: (data) => {
                    if (data.section === 'body' && data.column.index === 1) {
                        data.cell.text = [];
                    }
                },
                didDrawCell: (data) => {
                    if (!(data.section === 'body' && data.column.index === 1)) return;
                    const c = th.categories[data.row.index];
                    if (!c) return;
                    const ok = c.satisfied;
                    const txt = ok ? this.htT('충족') : this.htT('미충족');
                    const rgb = ok ? [...this.hyundai_colors_rgb.blue] : [183, 65, 56];
                    // 점 테두리: fill보다 한 단계 어둡게 → 흰 배경·연레드 tint 양쪽에서 분리
                    const ring = ok ? [0, 30, 66] : [150, 46, 40];

                    // 폰트/사이즈는 측정 전에 한 번만 설정 (getTextWidth가 현재 폰트 상태에 의존)
                    doc.setFont(this.htFam(), 'bold');
                    doc.setFontSize(8.5);

                    const r = 2.2;
                    const gap = 5;
                    const tw = doc.getTextWidth(txt);
                    const groupW = r * 2 + gap + tw;
                    const cx = data.cell.x + data.cell.width / 2;
                    const cy = data.cell.y + data.cell.height / 2;
                    const startX = cx - groupW / 2;
                    const dotCx = startX + r;
                    const textX = startX + r * 2 + gap;
                    const opticalCy = cy + 0.3;   // NotoSansKR middle-baseline 보정 (점과 공유)

                    doc.setLineWidth(0.5);
                    doc.setDrawColor(...ring);
                    doc.setFillColor(...rgb);
                    doc.circle(dotCx, opticalCy, r, 'FD');

                    doc.setTextColor(...rgb);
                    doc.text(txt, textX, opticalCy, { align: 'left', baseline: 'middle' });

                    // 상태 복원: autoTable이 didDrawCell 이후 grid border를 그림 (상태 상속 방지)
                    doc.setLineWidth(0.5);
                    doc.setTextColor(64, 64, 64);
                    doc.setDrawColor(228, 220, 211);
                },
                didDrawPage: (h) => this.addHeader(h.doc)
            });
            return doc.lastAutoTable.finalY;
        },

        // ============================================================
        // PAGE: {테마} 개선 과제  (paginated; clones renderCodeDetailBlock_v2)
        // ============================================================
        htPageThemeImprovement (doc, d, key) {
            const th = d.themes[key];
            const items = th.indicators;
            const margin = this.jsPDFOpt.margin;
            const pageH = doc.internal.pageSize.getHeight();
            const bottomLimit = pageH - margin.bottom - 10;
            const blockGap = 14;
            const MAX_PER_PAGE = 3;

            doc.addPage();
            this.addHeader(doc);
            let position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, this.htT(`${th.prefix} 개선 과제`));
            let countOnPage = 0;

            items.forEach((it) => {
                const blockH = this.htRenderImprovementBlock(doc, position_y, key, it, { measure: true });
                const needed = (countOnPage > 0 ? blockGap : 0) + blockH;
                // 페이지당 최대 3개 + 잔여 공간 부족 시 새 페이지 (동적: 짧으면 3개, 길면 2개로 자동 조절)
                if (countOnPage >= MAX_PER_PAGE || (countOnPage > 0 && position_y + needed > bottomLimit)) {
                    doc.addPage();
                    this.addHeader(doc);
                    position_y = this.firstElementSpacing;
                    position_y = this.addTitle(doc, position_y, 1, this.htT(`${th.prefix} 개선 과제`));
                    countOnPage = 0;
                }
                if (countOnPage > 0) position_y += blockGap;
                position_y = this.htRenderImprovementBlock(doc, position_y, key, it);
                countOnPage += 1;
            });
            return position_y;
        },
        htRenderImprovementBlock (doc, position_y, key, it, opt = {}) {
            const margin = this.jsPDFOpt.margin;
            const leftX = margin.left;
            const fullW = this.pageContentWidth;
            const level = it.level;
            const ri = this.htRubricIndicator(key, it.code) || {};
            const lvlData = (ri.levels && ri.levels[level]) || {};
            const answerText = lvlData.answer ? this.htT(lvlData.answer, key) : (level === 'na' ? this.htLvlKO('na') : '');
            const riskText = this.htT(lvlData.risk || '', key);
            const taskText = this.htT(lvlData.task || '', key);

            // ── 높이 사전 계산 (측정 모드/그리기 공통) ──
            const hdrLeftW = 150;
            const hdrRightW = fullW - hdrLeftW;
            const hdrPadV = 8;
            const hdrPadX = 10;
            const codeLabel = `${it.code}.  ${it.topic}`;
            const descText = this.htT(ri.question || '');

            this.setFont(doc, null, null, 'bold');
            doc.setFontSize(11);
            const codeLabelLines = doc.splitTextToSize(codeLabel, hdrLeftW - hdrPadX * 2).slice(0, 3);
            const codeLabelH = doc.getTextDimensions(codeLabelLines, { lineHeightFactor: 1.15 }).h;

            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(9);
            const descLines = doc.splitTextToSize(descText, hdrRightW - hdrPadX * 2).slice(0, 4);
            const descH = doc.getTextDimensions(descLines, { lineHeightFactor: 1.4 }).h;
            const hdrH = Math.max(codeLabelH, descH) + hdrPadV * 2;

            const tabsH = 16;

            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(9);
            const ansLines = doc.splitTextToSize(`▪ ${answerText}`, fullW - 24);
            const ansH = doc.getTextDimensions(ansLines, { lineHeightFactor: 1.4 }).h + 14;

            const cardGap = 10;
            const halfW = (fullW - cardGap) / 2;
            const cardPadX = 10;
            const chipH = 18;
            const bodyPadTop = chipH + 8;
            const riskLines = doc.splitTextToSize(riskText || '-', halfW - cardPadX * 2);
            const taskLines = doc.splitTextToSize(taskText || '-', halfW - cardPadX * 2);
            const riskBodyH = doc.getTextDimensions(riskLines, { lineHeightFactor: 1.4 }).h;
            const taskBodyH = doc.getTextDimensions(taskLines, { lineHeightFactor: 1.4 }).h;
            const cardH = Math.max(riskBodyH, taskBodyH) + bodyPadTop + 10;

            const totalH = hdrH + 6 + tabsH + ansH + 8 + cardH;
            if (opt.measure) return totalH;

            // ── Section 1: 헤더 (light-sand 2-col) ──
            doc.setFillColor(245, 242, 242);
            doc.rect(leftX, position_y, fullW, hdrH, 'F');
            doc.setDrawColor(216, 216, 216);
            doc.setLineWidth(0.4);
            doc.line(leftX + hdrLeftW, position_y + 6, leftX + hdrLeftW, position_y + hdrH - 6);
            this.setFont(doc, null, null, 'bold');
            doc.setFontSize(11);
            doc.setTextColor(40, 40, 40);
            doc.text(codeLabelLines, leftX + hdrPadX, position_y + (hdrH - codeLabelH) / 2, { baseline: 'top' });
            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(9);
            doc.setTextColor(64, 64, 64);
            doc.setLineHeightFactor(1.4);
            doc.text(descLines, leftX + hdrLeftW + hdrPadX, position_y + (hdrH - descH) / 2, { baseline: 'top' });
            doc.setLineHeightFactor(1.15);
            position_y += hdrH + 6;

            // ── Section 2: 4단계 탭 ──
            const tabKeys = ['insufficient', 'average', 'excellent', 'outstanding'];
            const tabW = fullW / 4;
            tabKeys.forEach((lk, idx) => {
                const tx = leftX + idx * tabW;
                const isCurrent = level === lk;
                if (isCurrent) {
                    doc.setFillColor(...LEVEL_ACCENT_RGB[lk]);
                    doc.rect(tx, position_y, tabW, tabsH, 'F');
                    this.setFont(doc, null, null, 'bold');
                    doc.setTextColor(51, 51, 51);
                } else {
                    doc.setFillColor(255, 255, 255);
                    doc.rect(tx, position_y, tabW, tabsH, 'F');
                    this.setFont(doc, null, null, 'medium');
                    doc.setTextColor(150, 150, 150);
                }
                doc.setFontSize(10.5);
                doc.text(this.htLvlKO(lk), tx + tabW / 2, position_y + 11, { align: 'center' });
            });
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.4);
            doc.rect(leftX, position_y, fullW, tabsH, 'D');
            for (let i = 1; i < 4; i++) {
                const dx = leftX + i * tabW;
                doc.line(dx, position_y + 3, dx, position_y + tabsH - 3);
            }
            position_y += tabsH;

            // ── Section 3: 회사 답변 카드 ──
            doc.setFillColor(252, 252, 252);
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.4);
            doc.rect(leftX, position_y, fullW, ansH, 'FD');
            this.setFont(doc, null, null, 'normal');
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.setLineHeightFactor(1.4);
            doc.text(ansLines, leftX + 12, position_y + 9, { baseline: 'top' });
            doc.setLineHeightFactor(1.15);
            position_y += ansH + 8;

            // ── Section 4: 도출 리스크 / 개선 과제 2-card ──
            const drawCard = (x, chipColor, chipTextColor, chipLabel, lines) => {
                doc.setFillColor(255, 255, 255);
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.4);
                doc.rect(x, position_y, halfW, cardH, 'FD');
                doc.setFillColor(...chipColor);
                doc.rect(x, position_y, halfW, chipH, 'F');
                this.setFont(doc, null, null, 'bold');
                doc.setFontSize(10);
                doc.setTextColor(...chipTextColor);
                doc.text(chipLabel, x + cardPadX, position_y + chipH / 2 + 1, { baseline: 'middle' });
                this.setFont(doc, null, null, 'normal');
                doc.setFontSize(9);
                doc.setTextColor(60, 60, 60);
                doc.setLineHeightFactor(1.4);
                doc.text(lines, x + cardPadX, position_y + bodyPadTop, { baseline: 'top' });
                doc.setLineHeightFactor(1.15);
            };
            // 도출 리스크 = 레벨 색과 분리, 회색 배경 (조희진님 피드백: 경영수준 박스와 색이 겹쳐 안 보임)
            drawCard(leftX, [110, 110, 110], [255, 255, 255], this.htT('도출 리스크'), riskLines);
            drawCard(leftX + halfW + cardGap, this.hyundai_colors_rgb.blue, [255, 255, 255], this.htT('개선 과제'), taskLines);
            position_y += cardH;

            return position_y;
        },

        // ============================================================
        // PAGE: Appendix (참고 URL)
        // ============================================================
        htPageAppendix (doc) {
            doc.addPage();
            this.addHeader(doc);
            const margin = this.jsPDFOpt.margin;
            const leftX = margin.left;
            let position_y = this.firstElementSpacing;

            position_y = this.addTitle(doc, position_y, 1, this.htT('Appendix (참고 URL)'));

            appendixUrls.forEach((grp) => {
                // '한국 유관 정부·공공기관' 그룹은 새 페이지에서 시작
                if (grp.group === '한국 유관 정부·공공기관') {
                    doc.addPage();
                    this.addHeader(doc);
                    position_y = this.firstElementSpacing;
                    position_y = this.addTitle(doc, position_y, 1, this.htT('Appendix (참고 URL)'));
                }
                position_y = this.addTitle(doc, position_y, 3, this.htT(grp.group), { skip_divider: true });
                autoTable(doc, {
                    startY: position_y,
                    margin: { left: leftX, right: margin.right },
                    theme: 'grid',
                    showHead: 'everyPage',
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    head: [[this.htT(grp.col), 'URL']],
                    headStyles: { ...this.tableStyles.th, fontSize: 8.5, halign: 'left' },
                    styles: { ...this.tableStyles.global, ...this.tableStyles.td, fontSize: 7.5, cellPadding: 3 },
                    body: grp.rows.map((r) => [this.htT(r[0]), { content: r[1], styles: { textColor: this.hyundai_colors.teal } }]),
                    columnStyles: { 0: { cellWidth: 165 } },
                    // URL 셀에 클릭 가능한 링크(새 창/기본 브라우저) 부착
                    didDrawCell: (data) => {
                        if (data.section === 'body' && data.column.index === 1) {
                            const url = grp.rows[data.row.index] && grp.rows[data.row.index][1];
                            if (url) {
                                doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url });
                            }
                        }
                    },
                    didDrawPage: (h) => this.addHeader(h.doc)
                });
                position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            });
            return position_y;
        },

        // ============================================================
        // chrome — hyundai-v2 addHeader/addFooter 복제
        // ============================================================
        addHeader (doc) {
            doc.setFillColor(this.hyundai_colors.blue);

            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.headerStyles.fontSize);
            doc.setTextColor(...this.jsPDFOpt.headerStyles.textColor);
            const [headerBox_position_x, headerBox_position_y] = [this.jsPDFOpt.margin.left, this.jsPDFOpt.margin.top + 10];
            doc.rect(headerBox_position_x, headerBox_position_y, this.pageContentWidth, this.jsPDFOpt.headerHeight, 'F');

            const report_header_padding = 10;
            let _report_serial_number = this.formatDate(new Date().toUTCString(), 'YYMMDD');
            _report_serial_number += `-${this.reportData.info.company_id}`;
            _report_serial_number += `-${this.reportData.info.id}`;

            _report_serial_number = doc.splitTextToSize(`${this.$t('발급번호', this.report_lang)} : ${_report_serial_number}`, this.pageContentWidth / 2);
            const _report_serial_number_dim = doc.getTextDimensions(_report_serial_number);
            const _report_serial_number_position_y = headerBox_position_y + ((this.jsPDFOpt.headerHeight - _report_serial_number_dim.h)) - 1;
            doc.text(_report_serial_number, headerBox_position_x + report_header_padding, _report_serial_number_position_y);

            const _company_name = this.companyInfo['회사명'] ?? '';
            const _report_company_header = doc.splitTextToSize(_company_name, doc.getTextWidth(_company_name) + 10);
            const _report_company_header_dim = doc.getTextDimensions(_report_company_header);
            const _report_company_header_position_x = this.jsPDFOpt.margin.left + this.pageContentWidth - doc.getTextWidth(_company_name) - report_header_padding;
            const _report_company_header_position_y = headerBox_position_y + ((this.jsPDFOpt.headerHeight - _report_company_header_dim.h)) - 1;

            if (this.report_lang === 'ca' && this.isChineseOrHanzi(_company_name)) {
                this.setFont(doc, null, this.jsPDFOpt.fontFamily_ca);
            } else {
                this.setFont(doc, null, this.jsPDFOpt.fontFamily);
            }

            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.headerStyles.fontSize);
            doc.setTextColor(...this.jsPDFOpt.headerStyles.textColor);
            doc.text(_report_company_header, _report_company_header_position_x, _report_company_header_position_y);
        }
        // addFooter 는 core.mixin 의 것을 그대로 사용 (좌측 QESG 컬러 로고 + 우측 페이지번호, 기존 HMC 결과보고서와 동일)
    }
};
